/**
 * O que é comum aos itens de construção com régua e compasso.
 *
 * OS PONTOS DA CONSTRUÇÃO. Como no Euclidea, todo cruzamento de dois objetos que o
 * aluno traçou já é um ponto da construção — marcado ou não. Quem levanta a
 * perpendicular por A e traça uma paralela a AB tem, no cruzamento, o vértice do
 * trapézio; quem traça as circunferências de centro P e de centro Q por A tem, no
 * segundo cruzamento, o simétrico A'. Exigir o clique ali seria corrigir o clique, e
 * não a geometria.
 *
 * O cruzamento é DERIVADO dos objetos traçados: na checagem de robustez, ele acompanha
 * a perturbação como qualquer ponto construído. E só vale se estiver no trecho
 * desenhado — um segmento não se cruza com nada além das suas pontas.
 */

import type { GeoId, GeoObject, GPoint } from '../../geom/construction.ts';
import { geoId, intersectionPoints } from '../../geom/construction.ts';
import type { Vec2 } from '../../geom/vec.ts';
import { dist } from '../../geom/vec.ts';

/** O ponto está no trecho desenhado do objeto? Reta inteira, semirreta, segmento; circunferência inteira. */
function noTracado(o: GeoObject, x: Vec2): boolean {
  if (o.kind !== 'line' || o.span === 'line') return true;
  const t = (x.x - o.p.x) * o.dir.x + (x.y - o.p.y) * o.dir.y;
  const eps = 1e-7 * (1 + dist(o.p, o.q));
  if (o.span === 'ray') return t >= -eps;
  return t >= -eps && t <= dist(o.p, o.q) + eps;
}

/**
 * O LADO PQ ESTÁ TRAÇADO? Vale qualquer traço da construção — segmento, semirreta ou
 * reta — que passe pelos dois vértices com os dois no trecho desenhado. A perpendicular
 * por A que já contém D é o lado AD: exigir um segmento por cima dela seria cobrar um
 * movimento que não acrescenta geometria nenhuma. `eps` é a tolerância de distância, em
 * unidades do desenho.
 */
export function ladoTracado(
  objs: ReadonlyMap<GeoId, GeoObject>,
  P: Vec2,
  Q: Vec2,
  eps: number,
): boolean {
  if (dist(P, Q) <= eps) return false;
  for (const o of objs.values()) {
    if (o.kind !== 'line') continue;
    const fora = (x: Vec2): number => Math.abs((x.x - o.p.x) * o.dir.y - (x.y - o.p.y) * o.dir.x);
    if (fora(P) > eps || fora(Q) > eps) continue;
    const t = (x: Vec2): number => (x.x - o.p.x) * o.dir.x + (x.y - o.p.y) * o.dir.y;
    const noTrecho = (x: Vec2): boolean =>
      o.span === 'line' ||
      (o.span === 'ray' ? t(x) >= -eps : t(x) >= -eps && t(x) <= dist(o.p, o.q) + eps);
    if (noTrecho(P) && noTrecho(Q)) return true;
  }
  return false;
}

/**
 * Os pontos do aluno — os que ele marcou e os cruzamentos do que ele traçou —, sem os
 * objetos do enunciado (`dados`). `escala` é um comprimento típico da figura, para a
 * tolerância de "mesmo ponto".
 */
export function pontosDaConstrucao(
  objs: ReadonlyMap<GeoId, GeoObject>,
  dados: ReadonlySet<string>,
  escala: number,
): GPoint[] {
  const doAluno = [...objs.values()].filter((o) => !dados.has(o.id as string));
  const marcados = doAluno.filter((o): o is GPoint => o.kind === 'point');
  const tracados = doAluno.filter((o) => o.kind !== 'point');
  const extras: GPoint[] = [];
  const eps = 1e-6 * Math.max(escala, 1);
  for (let i = 0; i < tracados.length; i++) {
    for (let j = i + 1; j < tracados.length; j++) {
      const a = tracados[i] as GeoObject;
      const b = tracados[j] as GeoObject;
      intersectionPoints(a, b).forEach((x, k) => {
        if (!noTracado(a, x) || !noTracado(b, x)) return;
        if ([...marcados, ...extras].some((q) => dist(q.pos, x) < eps)) return;
        extras.push({
          id: geoId(`x:${a.id as string}:${b.id as string}:${k}`),
          kind: 'point',
          def: { via: 'intersection', a: a.id, b: b.id, branch: k === 0 ? 0 : 1 },
          name: '',
          pos: x,
        });
      });
    }
  }
  return [...marcados, ...extras];
}
